import {
  Box,
  Button,
  IconButton,
  Stack,
  SxProps,
  Typography,
  styled,
} from '@mui/material';
import { Trip } from 'src/types/trip';
import DepartureCard from './DepartureCard';
import { useCallback, useEffect, useRef, useState } from 'react';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NoDeparturesWidget from './NoDeparturesWidget';
import GlobalAvailabilityWidget, {
  OpenerProps,
} from 'src/components/DatesPicker/GlobalAvailabilityWidget';
import { Departure } from 'src/services/departures/departures.model';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import useCompactView from 'src/hooks/useCompactView';
import { DatesAndPrices } from '../DatesAndPrices/DatesAndPrices';
import { trackDatesAndPricingEvent } from 'src/utils/AnalyticsTracking';
import { useMatchingDepartures } from 'src/hooks/useMatchingDepartures';
import DateRangeIcon from '@mui/icons-material/DateRange';

interface DepartureListProps {
  departures: Departure[];
  trip: Trip;
}

const DepartureDateFilterOpener: React.FC<OpenerProps & { sx?: SxProps }> = ({
  label,
  onClick,
  sx = {},
}) => {
  return (
    <Typography
      variant="Body S"
      sx={{
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 500,
        ...sx,
      }}
      onClick={onClick}
    >
      {label}
    </Typography>
  );
};

const DeparturesList: React.FC<DepartureListProps> = ({ departures, trip }) => {
  const { allDeparturesCounts } = useMatchingDepartures();
  const isCompactView = useCompactView();
  const boxRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScroll = useCallback(() => {
    if (boxRef.current) {
      const shouldShowLeftArrow = !isCompactView && boxRef.current.scrollLeft > 0;
      const shouldShowRightArrow =
        !isCompactView &&
        boxRef.current.scrollLeft + boxRef.current.clientWidth + 50 <
          boxRef.current.scrollWidth;

      if (showLeftArrow !== shouldShowLeftArrow) {
        setShowLeftArrow(shouldShowLeftArrow);
      }

      if (showRightArrow !== shouldShowRightArrow) {
        setShowRightArrow(shouldShowRightArrow);
      }
    }
  }, [isCompactView, showLeftArrow, showRightArrow]);

  useEffect(() => {
    checkScroll();
  }, [checkScroll]);

  const [isDatesOpen, setIsDatesOpen] = useState(false);
  const [departureId, setDepartureId] = useState<string | undefined>(undefined);
  const scroll = (direction: 'left' | 'right') => {
    if (boxRef.current) {
      const boxWidth = boxRef.current.clientWidth;
      boxRef.current.scrollBy({
        left: direction === 'right' ? boxWidth : -boxWidth,
        behavior: 'smooth',
      });
      setTimeout(checkScroll, 300);
    }
  };

  const renderAllDeparturesButton = () => (
    <Button
      sx={{
        borderRadius: '4px',
        border: (theme) => `1px solid ${theme.palette.principal.grey50}`,
        paddingInline: '8px',
        paddingBlock: '4px',
      }}
    >
      <Typography
        variant="Body S"
        fontWeight={500}
        onClick={() => {
          trackDatesAndPricingEvent({
            trip_code: trip.code,
            trip: trip.title,
            location: 'departures-list',
          });
          setDepartureId(undefined);
          setIsDatesOpen(true);
        }}
      >
        View all departures
      </Typography>
    </Button>
  );

  return (
    <Stack gap="8px">
      {isDatesOpen && (
        <DatesAndPrices
          tripCode={trip.code}
          dialogOpen={isDatesOpen}
          departureId={departureId}
          onClose={() => setIsDatesOpen(false)}
        />
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="16px">
        <Box display="flex" gap="4px" flexWrap="wrap" sx={{ alignItems: 'center' }}>
          <Typography variant="Body S">
            {departures.length} departures based on
          </Typography>
          <EventAvailableIcon />
          <GlobalAvailabilityWidget
            Opener={(props) => {
              return (
                <DepartureDateFilterOpener
                  {...props}
                  label={'your availability'}
                  isOpen={true}
                />
              );
            }}
          />
        </Box>
        {!isCompactView && renderAllDeparturesButton()}
      </Box>
      <DepartureCardsList
        showLeftArrow={showLeftArrow}
        showRightArrow={showRightArrow}
        scroll={scroll}
        boxRef={boxRef}
        departures={departures}
        trip={trip}
        isCompactView={isCompactView}
        allDeparturesCount={allDeparturesCounts?.[trip.code] ?? 0}
        isDatesOpen={setIsDatesOpen}
        setDepartureId={setDepartureId}
      />
      {isCompactView && renderAllDeparturesButton()}
    </Stack>
  );
};

const DepartureCardsList: React.FC<{
  showLeftArrow: boolean;
  showRightArrow: boolean;
  scroll: (direction: 'left' | 'right') => void;
  boxRef: React.RefObject<HTMLDivElement>;
  departures: Departure[];
  trip: Trip;
  isCompactView: boolean;
  allDeparturesCount: number;
  isDatesOpen: (open: boolean) => void;
  setDepartureId: (id: string) => void;
}> = ({
  showLeftArrow,
  showRightArrow,
  scroll,
  boxRef,
  departures,
  trip,
  isCompactView,
  allDeparturesCount,
  isDatesOpen,
  setDepartureId,
}) => {
  return (
    <>
      <Box position="relative">
        {showLeftArrow && (
          <ButtonBox direction={'left'}>
            <ArrowButton onClick={() => scroll('left')} direction="left">
              <NavigateBeforeIcon />
            </ArrowButton>
          </ButtonBox>
        )}
        <Box
          ref={boxRef}
          sx={{
            gap: '12px',
            overflowX: { xs: 'auto', sm: 'hidden', md: 'scroll' },
            display: 'flex',
            maxWidth: { md: '768px' },
            scrollbarWidth: 'none',
            overflow: isCompactView ? 'scroll' : 'hidden',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            width: '100%',
          }}
        >
          <>
            {departures.length === 0 && (
              <NoDeparturesWidget DepartureDateFilterOpener={DepartureDateFilterOpener} />
            )}
            {departures.map((departure) => (
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  isDatesOpen(true);
                  setDepartureId(departure.id);
                  trackDatesAndPricingEvent({
                    trip_code: trip.code,
                    trip: trip.title,
                    location: window.location.href.includes(trip.url)
                      ? 'departure-card-trip-page'
                      : 'departure-card-all-trips',
                  });
                }}
                key={departure.id}
              >
                <DepartureCard
                  key={departure.id}
                  departure={departure}
                  trip={trip}
                  sx={{ height: 'fit-content' }}
                />
              </Box>
            ))}
            {departures.length === 1 && !isCompactView && allDeparturesCount > 1 && (
              <MoreDeparturesInfoCard allDeparturesCount={allDeparturesCount} />
            )}
          </>
        </Box>
        {showRightArrow && (
          <>
            <ButtonBox direction={'right'}>
              <ArrowButton onClick={() => scroll('right')} direction="right">
                <NavigateNextIcon />
              </ArrowButton>
            </ButtonBox>
          </>
        )}
      </Box>
    </>
  );
};

interface ButtonBoxProps {
  direction: 'left' | 'right';
}

const ButtonBox = styled(Box)<ButtonBoxProps>(({ direction }) => ({
  '&:hover': { opacity: 0.7 },
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '150px',
  right: direction === 'right' ? 0 : 'unset',
  left: direction === 'left' ? 0 : 'unset',
  backgroundImage:
    direction == 'right'
      ? 'linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0) 50%)'
      : 'linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0) 50%)',
  zIndex: 1,
}));

const ArrowButton = styled(IconButton)<ButtonBoxProps>(({ direction }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  right: direction === 'right' ? 0 : 'unset',
  left: direction === 'left' ? 0 : 'unset',
  backgroundColor: '#D9D9D9',
  color: 'white',
  width: '24px',
  height: '24px',
  '&:hover': {
    backgroundColor: '#D9D9D9',
  },
}));

const MoreDeparturesInfoCard: React.FC<{ allDeparturesCount: number }> = ({
  allDeparturesCount,
}) => {
  const textStyle = {
    variant: 'Body S' as const,
    sx: {
      lineHeight: '19.6px',
      textWrap: 'nowrap',
    },
  };

  return (
    <Stack
      sx={{
        flex: 1,
        backgroundColor: (theme) => theme.palette.principal.blueLight,
        borderRadius: '8px',
        padding: '16px',
        height: 'fit-content',
      }}
    >
      <Typography
        variant="Body M bold"
        sx={{
          lineHeight: '19.2px',
          mb: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <DateRangeIcon />
        See more trip dates
      </Typography>
      <Typography {...textStyle}>
        This trip has {allDeparturesCount} planned departures.
      </Typography>
      <Stack direction="row" alignItems="center" gap="4px">
        <GlobalAvailabilityWidget
          Opener={(props) => (
            <DepartureDateFilterOpener
              {...props}
              label={'Expand your availability'}
              isOpen={true}
              sx={{ ...textStyle.sx, flex: 1 }}
            />
          )}
        />
        <Typography {...textStyle}>to see more dates</Typography>
      </Stack>
    </Stack>
  );
};

export default DeparturesList;
